
import { useSidClient } from "@/plugins/auth";
import { useRouter } from "vue-router";
import { defineComponent, reactive, ref } from "vue";
import { Field, Form } from "vee-validate";
import { checkNewPassword, checkNewPasswordRetype } from "@/commons/inputCheckCommon";
import * as Yup from "yup";
import { PASSWORD_MAX_LENGTH } from "@/commons/constants";
import useDeterring from "@/commons/useDeterring";
import { QuestionnaireItem } from "@/modules/questionnaire/questionnaireInfo";
import { isAuthFailed, isSiteRegistered } from "@/commons/commonUtility";
import { ALL_LINKAGE_ID, getLinkIdFromSiteCode, isQualified } from "@/commons/questionnaire/questionnaire";
import { QuestionnaireUserItem } from "@/modules/questionnaire/questionnaireUserItem";

export default defineComponent({
    name: "QuestionnaireNewPassword",
    components: {
        Form,
        Field
    },
    setup() {
        const sidClient = useSidClient();
        const router = useRouter();
        const { isDeterringRef, setNotRelease, asyncDeterringWrap } = useDeterring();
        const { questionnaireInfo, fetchToken, routeNames } = QuestionnaireItem.inject();
        const userBaseItem = QuestionnaireUserItem.inject();

        const newPasswordRef = ref("");
        const newPasswordRetypeRef = ref("");
        const errorMsgRef = ref("");

        const schema = Yup.object().shape({
            newPassword: checkNewPassword,
            newPasswordRetype: checkNewPasswordRetype
        });

        const newPasswordToggle = reactive<{
            fieldType: string;
            isOn: boolean;
        }>({
            fieldType: "password",
            isOn: false
        });

        const newPasswordRetypeToggle = reactive<{
            fieldType: string;
            isOn: boolean;
        }>({
            fieldType: "password",
            isOn: false
        });

        /**
         * パスワード表示・非表示切替
         * @param toggle { fieldType: string; isOn: boolean }
         */
        const switchPasswordFieldType = (toggle: { fieldType: string; isOn: boolean }) => {
            if (toggle.fieldType === "password") {
                toggle.fieldType = "text";
                toggle.isOn = true;
            } else {
                toggle.fieldType = "password";
                toggle.isOn = false;
            }
            return toggle;
        };

        /**
         * パスワード設定
         * @async
         *
         */
        const setPassword = async () => {
            let userName;
            try {
                // パスワード設定要求
                await sidClient.continueSignInNewPassword(newPasswordRef.value);
                // 認証ユーザ名
                userName = (await sidClient.getUserData()).Username;

                // 基本属性情報取得
                await userBaseItem.fetchUserData(sidClient);

                // ユーザ名格納
                userBaseItem.userBase.userName = userName;

                // セッションストレージ格納
                userBaseItem.storeStorage();
            } catch (e) {
                if (isAuthFailed(e)) {
                    // ログイン画面へ遷移
                    await router.push({ name: routeNames.LOGIN, params: { loginErrorMsg: "認証の有効期限が切れました。再ログイン後にもう一度最初から操作をお願いします。" } });
                } else {
                    if (e.message === "Failed to get information.") {
                        await router.push({ name: routeNames.GENERAL_ERROR, params: { message: "お手数をおかけしますが、しばらく時間を置いてから、再度操作していただけますようお願いいたします。" } });
                    } else {
                        errorMsgRef.value = "パスワード設定に失敗しました。後ほど再度お試し下さい。";   
                    }
                }
                return;
            }

            const linkId = getLinkIdFromSiteCode(questionnaireInfo.siteCode) as number;

            // サイト登録有無チェック
            if (linkId !== ALL_LINKAGE_ID && !isSiteRegistered(linkId, userBaseItem.userBase)) {
                // ログイン画面へ戻し、エラーメッセージを表示する
                await router.push({ name: routeNames.LOGIN, params:{ isServiceNotRegError: "true" } });
                return;
            }

            // メルマガ購読に関して条件があるか？
            if (questionnaireInfo.mmFlg || questionnaireInfo.mmExtId) {
                // メルマガ購読に関する条件に該当するか？
                if (!await isQualified(linkId, questionnaireInfo)) {
                    await router.push({ name: routeNames.LOGIN, params:{ isMmNotRegError: "true" } });
                    return;
                }
            }

            // トークンをコールバックする
            try {
                await fetchToken(sidClient);
                setNotRelease();
                window.location.href = questionnaireInfo.callbackUrlWithToken as string;
            } catch (e) {
                await router.push({ name: routeNames.GENERAL_ERROR, params: { message: "お手数をおかけしますが、しばらく時間を置いてから、再度操作していただけますようお願いいたします。" } });
            }
        };

        return {
            isDeterringRef,
            asyncDeterringWrap,
            newPasswordRef,
            newPasswordRetypeRef,
            errorMsgRef,
            schema,
            PASSWORD_MAX_LENGTH,
            newPasswordToggle,
            newPasswordRetypeToggle,
            switchPasswordFieldType,
            setPassword,
        };
    },
});
